import { useEffect, useRef, useState } from 'react';

import { cn } from '~/styles/extended-theme';

import { Spinner } from '../ui/spinner';

interface BackgroundThumbnailWithFallbackProps {
  className?: string;
  url: string | null | undefined;
  fallbackUrl?: string | null | undefined;
  alt?: string;
  rounded?: boolean;
  maxRetries?: number;
  initialBackoff?: number;
  backoffFactor?: number;
  showSpinnerWhileLoading?: boolean;
  spinnerColor?: 'white' | 'primary';
}

export default function BackgroundThumbnailWithFallback({
  className,
  url: urlProp,
  fallbackUrl,
  alt,
  rounded,
  maxRetries = 5,
  initialBackoff = 2000,
  backoffFactor = 500,
  showSpinnerWhileLoading = false,
  spinnerColor = 'white',
}: BackgroundThumbnailWithFallbackProps) {
  const [url, setUrl] = useState(urlProp);
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);
  const numRetries = useRef(0);
  const [imgElRetries, setImgElRetries] = useState(0);

  useEffect(() => {
    if (!url) return;

    const handleImageLoad = () => {
      setImageLoaded(true);
    };

    const handleImageError = () => {
      if (numRetries.current <= maxRetries) {
        setTimeout(() => loadImage(url), initialBackoff + backoffFactor * numRetries.current);
        numRetries.current = numRetries.current + 1;
      } else {
        if (fallbackUrl) {
          setUrl(fallbackUrl);
        }
      }
    };

    const loadImage = (url: string) => {
      const img = new Image();
      img.onload = handleImageLoad;
      img.onerror = handleImageError;
      img.src = url;
    };

    loadImage(url);
  }, [url, fallbackUrl, maxRetries, initialBackoff, backoffFactor]);

  return (
    <>
      {imageLoaded && url ? (
        <img
          src={url}
          alt={alt}
          className={cn(
            'max-h-full max-w-full object-contain',
            rounded ? 'rounded-2xl' : null,
            className,
          )}
          key={`${url}-${imgElRetries}`}
          onError={() => {
            if (imgElRetries <= 3) {
              setTimeout(() => setImgElRetries(imgElRetries + 1), 1500);
            }
          }}
        />
      ) : (
        <div className={cn('h-full w-full bg-gray-200 opacity-100', rounded ? 'rounded-2xl' : null)}>
          {showSpinnerWhileLoading ? (
            <Spinner
              className="absolute bottom-0 left-0 right-0 top-0 m-auto"
              color={spinnerColor}
              showSpinner
              size="sm"
            />
          ) : null}
        </div>
      )}
    </>
  );
}
